/**
 * IrrigationPlanSectors component
 * Displays the sectors tab with the steps panel
 */

import IrrigationPlanStepsPanel2 from "@/pages/main/components/IrrigationPlanStepsPanel2";
import type { IrrigationPlanStepFormData } from "@/pages/main/IrrigationPlanWizardPage";
import type { IrrigationPlanFormData } from "@/pages/main/IrrigationPlanWizardPage";
import type { PlanScheduleLike } from "@/pages/main/utils/irrigationPlanOverlap";

// Use any to avoid type mismatches with AUT types
type Sector = any;

interface IrrigationPlanSectorsProps {
  /** ID of the project */
  projectId: string;
  /** ID of the plan (undefined in create mode) */
  planId?: string;
  /** Plan configuration data */
  planData: IrrigationPlanFormData;
  /** Current plan steps */
  steps: IrrigationPlanStepFormData[];
  /** Available sectors in the project */
  sectors: Sector[];
  /** Whether the project has frequency inverter */
  hasFrequencyInverter: boolean;
  /** Schedules of other plans for validation */
  existingPlans: PlanScheduleLike[];
  /** Minimum gap between plans in minutes */
  minimumGapMinutes: number;
  /** Whether schedule resumption is enabled for the project's LIC */
  resumptionEnabled: boolean;
  /** Minimum gap (minutes) applied when resumption is disabled */
  withoutResumptionGap: number;
  /** Mirror data for all steps */
  mirrors?: Array<{
    id: string;
    source_irrigation_plan_step: string;
    target_sector: { id: string; name: string };
  }>;
  /** Handler for adding a step */
  onAddStep: () => void;
  /** Handler for removing a single step */
  onRemoveStep: (stepId: string) => Promise<void>;
  /** Handler for bulk removing steps */
  onBulkRemoveSteps: (stepIds: string[]) => Promise<void>;
  /** Handler for saving a step */
  onSaveStep: (step: IrrigationPlanStepFormData) => Promise<void>;
  /** Handler for updating a step locally */
  onUpdateStep: (stepId: string, updates: Partial<IrrigationPlanStepFormData>) => void;
  /** Handler for moving a step up/down */
  onMoveStep: (stepId: string, direction: "up" | "down") => Promise<void>;
  /** Handler for reordering steps */
  onReorderSteps: (newOrder: IrrigationPlanStepFormData[]) => Promise<void>;
  /** Whether data is loading */
  loading: boolean;
  /** Whether the form is read-only */
  readOnly: boolean;
  /** Additional project data */
  projectData?: {
    pipe_wash_time_seconds: number | null;
  };
}

export function IrrigationPlanSectors({
  projectId,
  planId,
  planData,
  steps,
  sectors,
  hasFrequencyInverter,
  existingPlans,
  minimumGapMinutes,
  resumptionEnabled,
  withoutResumptionGap,
  mirrors,
  onAddStep,
  onRemoveStep,
  onBulkRemoveSteps,
  onSaveStep,
  onUpdateStep,
  onMoveStep,
  onReorderSteps,
  loading,
  readOnly,
  projectData,
}: IrrigationPlanSectorsProps) {
  return (
    <IrrigationPlanStepsPanel2
      projectId={projectId}
      planId={planId}
      planData={planData}
      steps={steps}
      sectors={sectors}
      hasFrequencyInverter={hasFrequencyInverter}
      existingPlans={existingPlans}
      minimumGapMinutes={minimumGapMinutes}
      resumptionEnabled={resumptionEnabled}
      withoutResumptionGap={withoutResumptionGap}
      onAddStep={onAddStep}
      onRemoveStep={onRemoveStep}
      onBulkRemoveSteps={onBulkRemoveSteps}
      onSaveStep={onSaveStep}
      onUpdateStep={onUpdateStep}
      onMoveStep={onMoveStep}
      onReorderSteps={onReorderSteps}
      mirrors={mirrors}
      loading={loading}
      readOnly={readOnly}
      projectData={projectData}
    />
  );
}
